import React from "react";
import Head from "next/head";
import Link from "next/link";
import Navigation from "@/layout/navigation";

export default function ForCompaniesPageFr() {
  return (
    <>
      <Head>
        <title>
          Featcal pour Cliniques | Suivi perte de poids à l’échelle
        </title>
        <meta
          name="description"
          content="Featcal aide les cliniques de perte de poids et équipes de santé métabolique à structurer le suivi entre visites avec tracking patient, revue IA des journaux, rappels d’adhérence et synthèses cliniques."
        />
      </Head>

      <header className="bg-gradient-to-b from-white from-20% to-blue-300 flex flex-col gap-16 items-center justify-between overflow-hidden px-8 xl:px-0 z-[51] relative pb-20">
        <Navigation />
        <div className="flex flex-col md:items-center md:justify-center gap-6 mt-2 z-10 md:text-center">
          <p className="text-sm font-semibold text-blue-700 bg-white/70 border border-blue-200 px-4 py-2 rounded-full w-fit md:mx-auto">
            Pour cliniques & centres médicaux
          </p>
          <h1 className="text-4xl md:text-5xl font-medium leading-tight max-w-3xl">
            Un suivi perte de poids que toute l’équipe peut opérer-avec
            constance.
          </h1>
          <p className="text-md max-w-2xl mx-auto text-slate-700">
            Featcal transforme le suivi entre visites en système opérationnel :
            tracking standardisé, support d’adhérence automatisé, et synthèses
            prêtes pour la revue clinique-sans augmenter la charge
            administrative.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:justify-center">
            <Link
              href="/start"
              className="bg-blue-600 text-white py-3 px-8 rounded-full font-semibold hover:bg-blue-700 transition-all duration-200 text-center"
            >
              Demander une démo
            </Link>
            <Link
              href="/#faq"
              className="bg-white text-slate-900 py-3 px-8 rounded-full font-semibold border border-blue-200 hover:bg-blue-50 transition-all duration-200 text-center"
            >
              Voir la FAQ
            </Link>
          </div>
        </div>
      </header>

      <main id="main-content" className="bg-white">
        <section className="py-16 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto bg-white border border-slate-200 rounded-3xl p-8">
            <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-3">
              Le problème : les programmes se cassent entre les rendez-vous
            </h2>
            <p className="text-slate-700 leading-relaxed">
              Beaucoup de centres s’appuient sur un patchwork d’apps, d’Excel et
              de messages. Les patients trackent de façon irrégulière, l’équipe
              passe du temps à relancer, et la donnée n’est pas claire avant la
              prochaine visite. Featcal comble le “trou” du suivi entre visites
              avec une expérience programme que les patients suivent
              vraiment-que l’objectif soit la perte de poids, la prise de poids,
              ou la santé métabolique.
            </p>
          </div>
        </section>

        <section className="py-20 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-8">
            {[
              {
                title: "Standardiser le parcours",
                desc: "Rendez le parcours cohérent entre praticiens et cohortes : mêmes suivis, même revue, même cadence.",
              },
              {
                title: "Plus d’adhérence entre visites",
                desc: "Le changement se fait entre les rendez-vous. Visibilité quotidienne, rappels et prochaines étapes maintiennent le momentum sans appels supplémentaires.",
              },
              {
                title: "Réduire la charge opérationnelle",
                desc: "Moins de relances manuelles, moins de messages dispersés, et des synthèses propres-pour se concentrer sur l’essentiel.",
              },
            ].map((card) => (
              <div
                key={card.title}
                className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-3xl p-8 shadow-xl shadow-blue-100/30"
              >
                <h2 className="text-xl font-semibold text-slate-900 mb-3">
                  {card.title}
                </h2>
                <p className="text-slate-700 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="py-20 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-start">
            <div className="bg-white border border-slate-200 rounded-3xl p-8">
              <h2 className="text-3xl md:text-4xl font-medium text-slate-900 mb-4">
                Conçu pour les équipes et les programmes (pas juste 1 praticien)
              </h2>
              <p className="text-slate-700 leading-relaxed mb-6">
                Un centre a besoin de plus que du tracking : il faut un
                programme qui se déploie. Featcal centralise les données
                déclaratives patient et standardise le suivi pour toute
                l’équipe.
              </p>
              <ul className="space-y-3 text-slate-700">
                {[
                  "Définissez les suivis par parcours (poids, repas, activité, sommeil, etc.).",
                  "Synthèses et tendances prêtes pour la revue clinique.",
                  "Nudges automatisés pour réduire abandons et no-shows.",
                  "Plans et prochaines étapes toujours accessibles au patient.",
                ].map((p) => (
                  <li key={p} className="flex gap-3">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-3xl p-8 shadow-xl shadow-blue-100/30">
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">
                Scénarios programmes typiques
              </h3>
              <ul className="space-y-3 text-slate-700">
                {[
                  "Programmes de perte de poids médicale avec suivi entre visites.",
                  "Parcours obésité coordonnés par IDE et médecins.",
                  "Santé métabolique / prédiabète centrés sur l’adhérence.",
                  "Suivi post-intervention après ajustements thérapeutiques.",
                ].map((p) => (
                  <li key={p} className="flex gap-3">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="bg-gradient-to-b from-white to-blue-50 py-20 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-medium text-slate-900 mb-4">
              Une expérience programme que les patients suivent vraiment
            </h2>
            <p className="text-slate-700 max-w-3xl mb-12">
              Une PWA simple côté patient + un système de revue côté équipe.
              Featcal crée des actions quotidiennes, réduit la friction et
              aligne tout le monde-pour exécuter le plan de soins avec
              constance.
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  title: "Suivi quotidien patient",
                  points: [
                    "Poids, repas, activité, sommeil, hydratation, entraînements",
                    "Objectifs clairs et progression visible",
                    "Moins de formulaires papier et d’apps dispersées",
                  ],
                },
                {
                  title: "Plans & ressources structurés",
                  points: [
                    "Plans livrés dans l’app (toujours à jour)",
                    "Recettes et consignes pour réduire la confusion",
                    "Rappels automatisés pour soutenir l’adhérence",
                  ],
                },
                {
                  title: "Revue clinique plus intelligente",
                  points: [
                    "Tendances qui mettent en avant risque et manque d’adhérence",
                    "Préparation plus rapide des consultations",
                    "Moins de nettoyage manuel de données",
                  ],
                },
                {
                  title: "Gains opérationnels",
                  points: [
                    "Moins de no-shows via rappels",
                    "Moins de bruit entrant pour les coordinateurs",
                    "Parcours patients plus cohérents à l’échelle",
                  ],
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="bg-white border border-blue-100 rounded-3xl p-8 shadow-xl shadow-blue-100/20"
                >
                  <h3 className="text-xl font-semibold text-slate-900 mb-4">
                    {item.title}
                  </h3>
                  <ul className="space-y-2 text-slate-700">
                    {item.points.map((p) => (
                      <li key={p} className="flex gap-3">
                        <span className="text-green-600 font-bold">✓</span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="mt-12 flex flex-col sm:flex-row gap-3">
              <Link
                href="/start"
                className="bg-blue-600 text-white py-3 px-8 rounded-full font-semibold hover:bg-blue-700 transition-all duration-200 text-center"
              >
                Demander une démo
              </Link>
              <Link
                href="/fr"
                className="bg-white text-slate-900 py-3 px-8 rounded-full font-semibold border border-blue-200 hover:bg-blue-50 transition-all duration-200 text-center"
              >
                Retour à l’accueil
              </Link>
            </div>
          </div>
        </section>

        <section className="py-20 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-medium text-slate-900 mb-4">
                Une implémentation qui ne ralentit pas l’équipe
              </h2>
              <p className="text-slate-700 leading-relaxed mb-8">
                Pas besoin d’un projet sur plusieurs mois pour améliorer le
                suivi. Featcal est conçu pour s’onboard rapidement et s’adapter
                à votre fonctionnement.
              </p>
              <div className="space-y-4">
                {[
                  {
                    title: "1) Définir votre parcours",
                    desc: "Choisissez les suivis et la fréquence, en fonction du programme et des rôles.",
                  },
                  {
                    title: "2) Lancer une cohorte pilote",
                    desc: "Démarrez avec un groupe pour valider la cadence, les messages et la revue.",
                  },
                  {
                    title: "3) Déployer à l’échelle",
                    desc: "Standardisez les templates une fois, puis déployez sur cohortes et sites.",
                  },
                  {
                    title: "4) Améliorer en continu",
                    desc: "Utilisez les signaux d’adhérence et les tendances pour intervenir plus tôt.",
                  },
                ].map((s) => (
                  <div
                    key={s.title}
                    className="bg-white border border-slate-200 rounded-2xl p-6"
                  >
                    <p className="font-semibold text-slate-900">{s.title}</p>
                    <p className="text-slate-700 mt-1">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-3xl p-8 shadow-xl shadow-blue-100/30">
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">
                Ce que les équipes améliorent généralement
              </h3>
              <ul className="space-y-3 text-slate-700">
                {[
                  "Plus d’adhérence entre visites (tracking et exécution du plan plus réguliers).",
                  "Moins de temps passé à relancer et organiser la donnée.",
                  "Consultations plus structurées avec tendances plus lisibles.",
                  "Expérience programme plus cohérente à l’échelle.",
                ].map((p) => (
                  <li key={p} className="flex gap-3">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link
                  href="/start"
                  className="bg-blue-600 text-white py-3 px-8 rounded-full font-semibold hover:bg-blue-700 transition-all duration-200 text-center"
                >
                  Demander une démo
                </Link>
                <Link
                  href="/fr/privacy-policy"
                  className="bg-white text-slate-900 py-3 px-8 rounded-full font-semibold border border-blue-200 hover:bg-blue-50 transition-all duration-200 text-center"
                >
                  Confidentialité & sécurité
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
